import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  MenuItem,
  TextField,
  Chip,
  FormControlLabel,
  Checkbox,
  TablePagination,
} from '@mui/material';
import { pricingApi } from '../services/api';

const UPLOAD_PROVIDERS = [
  { value: '', label: 'Auto-detect from filename' },
  { value: 'caseys', label: "Casey's" },
  { value: 'tapetro', label: 'TA / Petro' },
  { value: 'racetrac', label: 'RaceTrac' },
  { value: 'sappbros', label: 'Sapp Bros' },
  { value: 'ambest', label: 'AM Best' },
];

const FILTER_PROVIDERS = [{ value: '', label: 'All providers' }, ...UPLOAD_PROVIDERS.slice(1)];

const ISSUE_LABELS = {
  no_master_match: 'No master match',
  missing_zip: 'Missing zip',
  missing_coords: 'Missing coordinates',
  missing_address: 'Missing address',
};

const EMPTY_STATION_FORM = {
  sourceProvider: '',
  providerStationId: '',
  stationName: '',
  addressLine1: '',
  city: '',
  state: '',
  zip: '',
  latitude: '',
  longitude: '',
};

const MasterLocations = () => {
  const [file, setFile] = useState(null);
  const [uploadProvider, setUploadProvider] = useState('');
  const [uploading, setUploading] = useState(false);
  const [enrichProvider, setEnrichProvider] = useState('');
  const [enrichStationId, setEnrichStationId] = useState('');
  const [enriching, setEnriching] = useState(false);
  const [dryRun, setDryRun] = useState(true);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [enrichResult, setEnrichResult] = useState(null);
  const [counts, setCounts] = useState([]);
  const [loadingSummary, setLoadingSummary] = useState(false);

  const [browseProvider, setBrowseProvider] = useState('');
  const [browseSearch, setBrowseSearch] = useState('');
  const [browseQuery, setBrowseQuery] = useState({ provider: '', search: '' });
  const [browseRows, setBrowseRows] = useState([]);
  const [browseTotal, setBrowseTotal] = useState(0);
  const [browsePage, setBrowsePage] = useState(0);
  const [browseRowsPerPage, setBrowseRowsPerPage] = useState(25);
  const [loadingBrowse, setLoadingBrowse] = useState(false);

  const [reportProvider, setReportProvider] = useState('');
  const [reportLoading, setReportLoading] = useState(false);
  const [reportResult, setReportResult] = useState(null);

  const [stationForm, setStationForm] = useState(EMPTY_STATION_FORM);
  const [enrichAfterSave, setEnrichAfterSave] = useState(true);
  const [savingStation, setSavingStation] = useState(false);

  const loadSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const data = await pricingApi.getMasterLocationSummary();
      setCounts(data.counts || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load master location summary');
    } finally {
      setLoadingSummary(false);
    }
  }, []);

  const loadBrowse = useCallback(async () => {
    setLoadingBrowse(true);
    try {
      const data = await pricingApi.listMasterLocations({
        sourceProvider: browseQuery.provider || undefined,
        search: browseQuery.search || undefined,
        skip: browsePage * browseRowsPerPage,
        limit: browseRowsPerPage,
      });
      setBrowseRows(data.rows || []);
      setBrowseTotal(data.total || 0);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load master locations');
    } finally {
      setLoadingBrowse(false);
    }
  }, [browseQuery, browsePage, browseRowsPerPage]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadBrowse();
  }, [loadBrowse]);

  const handleUpload = async () => {
    if (!file) {
      setError('Choose a .csv file first');
      return;
    }
    setUploading(true);
    setError(null);
    setMessage(null);
    setLastResult(null);
    try {
      const result = await pricingApi.uploadMasterLocationCsv(file, uploadProvider || undefined);
      setLastResult(result);
      setMessage(
        `Imported ${result.rowCount} ${result.sourceProvider} locations — ` +
          `${result.upsert?.upserted ?? 0} new, ${result.upsert?.modified ?? 0} updated.`
      );
      setFile(null);
      await loadSummary();
      await loadBrowse();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleEnrich = async () => {
    setEnriching(true);
    setError(null);
    setMessage(null);
    setEnrichResult(null);
    try {
      const result = await pricingApi.enrichFuelPricesFromMasterLocations({
        dryRun,
        limit: 10000,
        sourceProvider: enrichProvider || undefined,
        stationId: enrichStationId.trim() || undefined,
      });
      setEnrichResult(result);
      const scope =
        enrichStationId.trim() && enrichProvider
          ? ` for ${enrichProvider} station ${enrichStationId.trim()}`
          : enrichProvider
            ? ` for ${enrichProvider}`
            : '';
      setMessage(
        dryRun
          ? `Dry run${scope}: ${result.matched} of ${result.scanned} fuel_prices rows would be enriched.`
          : `Updated ${result.updated} of ${result.scanned} fuel_prices rows${scope}.`
      );
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Enrichment failed');
    } finally {
      setEnriching(false);
    }
  };

  const handleBrowseSearch = () => {
    setBrowsePage(0);
    setBrowseQuery({ provider: browseProvider, search: browseSearch.trim() });
  };

  const handleMissingReport = async () => {
    setReportLoading(true);
    setError(null);
    setReportResult(null);
    try {
      const result = await pricingApi.getMissingFuelPriceDataReport({
        sourceProvider: reportProvider || undefined,
        limit: 200,
      });
      setReportResult(result);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load missing data report');
    } finally {
      setReportLoading(false);
    }
  };

  const prefillStationForm = (row) => {
    setStationForm({
      sourceProvider: row.sourceProvider || '',
      providerStationId: row.stationId || '',
      stationName: row.stationName || '',
      addressLine1: row.addressLine1 || '',
      city: row.city || '',
      state: row.state || '',
      zip: row.zip === '00000' ? '' : row.zip || '',
      latitude: row.latitude != null ? String(row.latitude) : '',
      longitude: row.longitude != null ? String(row.longitude) : '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSaveStation = async () => {
    if (!stationForm.sourceProvider || !stationForm.providerStationId.trim()) {
      setError('Provider and station ID are required');
      return;
    }
    setSavingStation(true);
    setError(null);
    setMessage(null);
    try {
      const payload = {
        sourceProvider: stationForm.sourceProvider,
        providerStationId: stationForm.providerStationId.trim(),
        stationName: stationForm.stationName.trim() || null,
        addressLine1: stationForm.addressLine1.trim() || null,
        city: stationForm.city.trim() || null,
        state: stationForm.state.trim() || null,
        zip: stationForm.zip.trim() || null,
      };
      if (stationForm.latitude !== '') payload.latitude = Number(stationForm.latitude);
      if (stationForm.longitude !== '') payload.longitude = Number(stationForm.longitude);

      const result = await pricingApi.upsertMasterLocation(payload);
      setMessage(
        `Saved master location ${result.row.sourceProvider}/${result.row.providerStationId}.`
      );
      await loadSummary();
      await loadBrowse();

      if (enrichAfterSave) {
        const enrichRes = await pricingApi.enrichFuelPricesFromMasterLocations({
          dryRun: false,
          sourceProvider: stationForm.sourceProvider,
          stationId: stationForm.providerStationId.trim(),
          limit: 5000,
        });
        setMessage(
          `Saved master location and enriched ${enrichRes.updated} fuel_prices row(s) for station ${stationForm.providerStationId.trim()}.`
        );
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to save master location');
    } finally {
      setSavingStation(false);
    }
  };

  const updateStationField = (field) => (e) => {
    setStationForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <Typography variant="h5" gutterBottom>
        Master location reference
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Upload provider station location CSVs into <code>northstar.ref_master_locations</code>. Use
        this reference to fill missing address, city, state, zip, and coordinates in{' '}
        <code>fuel_prices</code>. Re-upload when a provider adds new locations.
      </Typography>

      {message && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Add or update a single station
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Use this for one-off gaps (e.g. a station missing from the provider CSV). Saving upserts{' '}
          <code>ref_master_locations</code>; optionally enriches matching <code>fuel_prices</code>{' '}
          rows immediately.
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
          <TextField
            select
            label="Provider"
            size="small"
            required
            value={stationForm.sourceProvider}
            onChange={updateStationField('sourceProvider')}
            sx={{ minWidth: 160 }}
          >
            {UPLOAD_PROVIDERS.slice(1).map((p) => (
              <MenuItem key={p.value} value={p.value}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Station ID"
            size="small"
            required
            value={stationForm.providerStationId}
            onChange={updateStationField('providerStationId')}
            sx={{ minWidth: 120 }}
          />
          <TextField
            label="Station name"
            size="small"
            value={stationForm.stationName}
            onChange={updateStationField('stationName')}
            sx={{ minWidth: 200 }}
          />
          <TextField
            label="Address"
            size="small"
            value={stationForm.addressLine1}
            onChange={updateStationField('addressLine1')}
            sx={{ minWidth: 220 }}
          />
          <TextField
            label="City"
            size="small"
            value={stationForm.city}
            onChange={updateStationField('city')}
            sx={{ minWidth: 140 }}
          />
          <TextField
            label="State"
            size="small"
            value={stationForm.state}
            onChange={updateStationField('state')}
            sx={{ minWidth: 80 }}
          />
          <TextField
            label="Zip"
            size="small"
            value={stationForm.zip}
            onChange={updateStationField('zip')}
            sx={{ minWidth: 100 }}
          />
          <TextField
            label="Latitude"
            size="small"
            value={stationForm.latitude}
            onChange={updateStationField('latitude')}
            sx={{ minWidth: 120 }}
          />
          <TextField
            label="Longitude"
            size="small"
            value={stationForm.longitude}
            onChange={updateStationField('longitude')}
            sx={{ minWidth: 120 }}
          />
        </Box>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <FormControlLabel
            control={
              <Checkbox
                checked={enrichAfterSave}
                onChange={(e) => setEnrichAfterSave(e.target.checked)}
              />
            }
            label="Enrich fuel_prices for this station after save"
          />
          <Button variant="contained" onClick={handleSaveStation} disabled={savingStation}>
            {savingStation ? <CircularProgress size={22} color="inherit" /> : 'Save location'}
          </Button>
          <Button variant="text" onClick={() => setStationForm(EMPTY_STATION_FORM)}>
            Clear form
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Missing data in fuel_prices
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Stations with placeholder zip, missing coordinates, bad address, or no master location
          match. Click &quot;Add to form&quot; to pre-fill the single-station editor above.
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center" mb={2}>
          <TextField
            select
            label="Provider"
            size="small"
            value={reportProvider}
            onChange={(e) => setReportProvider(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            {FILTER_PROVIDERS.map((p) => (
              <MenuItem key={p.value || 'all'} value={p.value}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="outlined" onClick={handleMissingReport} disabled={reportLoading}>
            {reportLoading ? <CircularProgress size={22} /> : 'Run report'}
          </Button>
        </Box>
        {reportResult && (
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Scanned {reportResult.scannedStations} unique stations —{' '}
              {reportResult.stationsWithIssues} with issues
              {reportResult.truncated ? ' (showing first 200)' : ''}.
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Provider</TableCell>
                    <TableCell>stationId</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>City</TableCell>
                    <TableCell>ST</TableCell>
                    <TableCell>Zip</TableCell>
                    <TableCell>Issues</TableCell>
                    <TableCell width={100} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportResult.rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Typography variant="body2" color="text.secondary">
                          No stations with missing data found for this filter.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    reportResult.rows.map((row) => (
                      <TableRow key={`${row.sourceProvider}|${row.stationId}`}>
                        <TableCell>{row.sourceProvider}</TableCell>
                        <TableCell>{row.stationId}</TableCell>
                        <TableCell>{row.stationName || '—'}</TableCell>
                        <TableCell>{row.city || '—'}</TableCell>
                        <TableCell>{row.state || '—'}</TableCell>
                        <TableCell>{row.zip || '—'}</TableCell>
                        <TableCell>
                          <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {row.issues.map((issue) => (
                              <Chip
                                key={issue}
                                size="small"
                                label={ISSUE_LABELS[issue] || issue}
                                color={issue === 'no_master_match' ? 'warning' : 'default'}
                              />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Button size="small" onClick={() => prefillStationForm(row)}>
                            Add to form
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Browse master locations
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center" mb={2}>
          <TextField
            select
            label="Provider"
            size="small"
            value={browseProvider}
            onChange={(e) => setBrowseProvider(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            {FILTER_PROVIDERS.map((p) => (
              <MenuItem key={p.value || 'all'} value={p.value}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Search"
            size="small"
            placeholder="Station ID, name, city, address"
            value={browseSearch}
            onChange={(e) => setBrowseSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleBrowseSearch()}
            sx={{ minWidth: 280 }}
          />
          <Button variant="outlined" onClick={handleBrowseSearch} disabled={loadingBrowse}>
            Search
          </Button>
        </Box>
        {loadingBrowse ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Provider</TableCell>
                    <TableCell>Station ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>City</TableCell>
                    <TableCell>ST</TableCell>
                    <TableCell>Zip</TableCell>
                    <TableCell>Lat</TableCell>
                    <TableCell>Lng</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {browseRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <Typography variant="body2" color="text.secondary">
                          No master locations match your filter.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    browseRows.map((row) => (
                      <TableRow key={`${row.sourceProvider}|${row.providerStationId}`}>
                        <TableCell>{row.sourceProvider}</TableCell>
                        <TableCell>{row.providerStationId}</TableCell>
                        <TableCell>{row.stationName || '—'}</TableCell>
                        <TableCell>{row.addressLine1 || '—'}</TableCell>
                        <TableCell>{row.city || '—'}</TableCell>
                        <TableCell>{row.state || '—'}</TableCell>
                        <TableCell>{row.zip || '—'}</TableCell>
                        <TableCell>{row.latitude ?? '—'}</TableCell>
                        <TableCell>{row.longitude ?? '—'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={browseTotal}
              page={browsePage}
              onPageChange={(_, p) => setBrowsePage(p)}
              rowsPerPage={browseRowsPerPage}
              onRowsPerPageChange={(e) => {
                setBrowseRowsPerPage(parseInt(e.target.value, 10));
                setBrowsePage(0);
              }}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </>
        )}
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Upload provider location CSV
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center" mb={2}>
          <TextField
            select
            label="Provider"
            size="small"
            value={uploadProvider}
            onChange={(e) => setUploadProvider(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            {UPLOAD_PROVIDERS.map((p) => (
              <MenuItem key={p.value || 'auto'} value={p.value}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="outlined" component="label" disabled={uploading}>
            Select file
            <input
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </Button>
          <Typography variant="body2">{file ? file.name : 'No file selected'}</Typography>
          <Button variant="contained" onClick={handleUpload} disabled={uploading || !file}>
            {uploading ? <CircularProgress size={22} color="inherit" /> : 'Upload CSV'}
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" display="block">
          Supported formats: Caseys commercial stores, TA/Petro, RaceTrac, Sapp Bros, AM Best address files.
        </Typography>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Enrich fuel_prices from master locations
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Fills only blank fields (station name, address, city, state, zip, latitude, longitude).
          Matches by provider station id or city + state.
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <TextField
            select
            label="Provider"
            size="small"
            value={enrichProvider}
            onChange={(e) => setEnrichProvider(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            {FILTER_PROVIDERS.map((p) => (
              <MenuItem key={p.value || 'all'} value={p.value}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Station ID (optional)"
            size="small"
            value={enrichStationId}
            onChange={(e) => setEnrichStationId(e.target.value)}
            placeholder="e.g. 2700"
            sx={{ minWidth: 160 }}
          />
          <TextField
            select
            label="Mode"
            size="small"
            value={dryRun ? 'dry' : 'apply'}
            onChange={(e) => setDryRun(e.target.value === 'dry')}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="dry">Dry run (preview)</MenuItem>
            <MenuItem value="apply">Apply updates</MenuItem>
          </TextField>
          <Button variant="contained" color="secondary" onClick={handleEnrich} disabled={enriching}>
            {enriching ? <CircularProgress size={22} color="inherit" /> : 'Run enrichment'}
          </Button>
        </Box>
        {enrichResult?.samples?.length > 0 && (
          <Box mt={2}>
            <Typography variant="subtitle2" gutterBottom>
              Sample patches
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Provider</TableCell>
                  <TableCell>stationId</TableCell>
                  <TableCell>Fields to fill</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {enrichResult.samples.map((s, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{s.sourceProvider}</TableCell>
                    <TableCell>{s.stationId}</TableCell>
                    <TableCell>{Object.keys(s.patch || {}).join(', ')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>

      {lastResult && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Last upload
          </Typography>
          <Typography variant="body2">
            Provider: {lastResult.sourceProvider} · Rows: {lastResult.rowCount} · Total in DB:{' '}
            {lastResult.upsert?.total ?? '—'}
          </Typography>
        </Paper>
      )}

      <Paper sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="subtitle1">Locations in reference collection</Typography>
          <Button onClick={loadSummary} disabled={loadingSummary}>
            Refresh
          </Button>
        </Box>
        {loadingSummary ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : counts.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No locations loaded yet.
          </Typography>
        ) : (
          <Box display="flex" flexWrap="wrap" gap={1}>
            {counts.map((c) => (
              <Chip key={c._id} label={`${c._id}: ${c.count}`} />
            ))}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default MasterLocations;
